import { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { UNITS } from "@/lib/unitConversions";
import { Leaf, Wheat, Flame, MilkOff, Droplet, Sparkles } from "lucide-react";
import GlobalLanguageSwitcher from "@/components/GlobalLanguageSwitcher";
import usePageTitle from "@/hooks/usePageTitle";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Security deny-list — even if the backend misbehaves, the UI never shows these.
const FORBIDDEN_KEYS = new Set([
  "total_cost",
  "cost_per_serving",
  "target_margin",
  "plate_profit_percent",
  "unit_cost",
  "bulk_price",
  "bulk_quantity",
  "revenue_contribution",
  "subscription_tier",
  "recipe_count",
  "is_super_admin",
  "mobile_number",
  "phone",
]);

// Strip any forbidden field from a payload — defence-in-depth.
const scrubGuestPayload = (node) => {
  if (Array.isArray(node)) return node.map(scrubGuestPayload);
  if (node && typeof node === "object") {
    const out = {};
    for (const [k, v] of Object.entries(node)) {
      if (FORBIDDEN_KEYS.has(k)) continue;
      out[k] = scrubGuestPayload(v);
    }
    return out;
  }
  return node;
};

// Format price with the restaurant's configured currency symbol.
const formatPrice = (amount, symbol, position) => {
  const num = parseFloat(amount);
  if (Number.isNaN(num)) return "";
  const formatted = num.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return position === "suffix" ? `${formatted} ${symbol}` : `${symbol}${formatted}`;
};

// Convert a quantity between metric/imperial representations for guest display.
// Pure display helper — pricing is untouched.
const displayQty = (qty, unit, system) => {
  const q = parseFloat(qty);
  if (!qty || Number.isNaN(q) || !unit) return { qty: qty || "", unit: unit || "" };

  const info = UNITS[unit];
  if (!info) return { qty, unit };

  // Mass: metric=g/kg, imperial=oz/lb
  if (info.type === "mass") {
    if (system === "imperial") {
      const grams = q * info.factor;
      if (grams >= 453.592) {
        return { qty: (grams / 453.592).toFixed(2), unit: "lb" };
      }
      return { qty: (grams / 28.3495).toFixed(2), unit: "oz" };
    }
    const grams = q * info.factor;
    if (grams >= 1000) return { qty: (grams / 1000).toFixed(2), unit: "kg" };
    return { qty: grams.toFixed(0), unit: "g" };
  }

  // Volume: metric=ml/L, imperial=fl oz/cup
  if (info.type === "volume") {
    if (system === "imperial") {
      const ml = q * info.factor;
      if (ml >= 236.588) return { qty: (ml / 236.588).toFixed(2), unit: "cup" };
      return { qty: (ml / 29.5735).toFixed(2), unit: "fl oz" };
    }
    const ml = q * info.factor;
    if (ml >= 1000) return { qty: (ml / 1000).toFixed(2), unit: "L" };
    return { qty: ml.toFixed(0), unit: "ml" };
  }

  // Count — leave untouched
  return { qty, unit };
};

// Mapping of dietary tag value → icon component.
// Icons render slim and editorial, never garish.
const DIETARY_ICONS = {
  Veg: { Icon: Leaf, color: "text-emerald-700" },
  Vegan: { Icon: Leaf, color: "text-emerald-800" },
  "Gluten-Free": { Icon: Wheat, color: "text-amber-700" },
  "Dairy-Free": { Icon: MilkOff, color: "text-sky-700" },
  "Nut-Free": { Icon: Droplet, color: "text-stone-600" },
  Halal: { Icon: Sparkles, color: "text-emerald-700" },
  Kosher: { Icon: Sparkles, color: "text-blue-700" },
};

// Compact "meta strip": calories · dietary tag pills · spice flames.
// Used inline on the menu card and inside the detail sheet.
const MenuMetaStrip = ({ item, brandColor, dense = false }) => {
  const { t } = useTranslation();
  const tags = Array.isArray(item.dietary_tags) ? item.dietary_tags : [];
  const hasCalories = item.calories !== null && item.calories !== undefined && item.calories !== "";
  const hasSpice = Number.isInteger(item.spice_level) && item.spice_level > 0;
  if (!hasCalories && tags.length === 0 && !hasSpice) return null;

  return (
    <div
      className={`flex flex-wrap items-center gap-x-3 gap-y-1.5 ${
        dense ? "mt-2" : "mt-3"
      }`}
      data-testid={`menu-meta-${item.id}`}
      style={{ fontFamily: "'Manrope', sans-serif" }}
    >
      {hasCalories && (
        <span
          className="inline-flex items-center text-[0.68rem] uppercase tracking-[0.18em] text-stone-500"
          data-testid={`menu-calories-${item.id}`}
        >
          {item.calories} kcal
        </span>
      )}
      {tags.map((tag) => {
        const meta = DIETARY_ICONS[tag];
        const Icon = meta?.Icon || Leaf;
        const label = t(`dietaryTags.${tag}`, { defaultValue: tag });
        return (
          <span
            key={tag}
            className={`inline-flex items-center gap-1 text-[0.68rem] uppercase tracking-[0.16em] ${meta?.color || "text-stone-600"}`}
            title={label}
            data-testid={`menu-dietary-${item.id}-${tag}`}
          >
            <Icon className="h-3.5 w-3.5" strokeWidth={1.6} />
            {label}
          </span>
        );
      })}
      {hasSpice && (
        <span
          className="inline-flex items-center gap-0.5"
          title={
            [
              t("spiceLevels.mild"),
              t("spiceLevels.medium"),
              t("spiceLevels.hot"),
            ][item.spice_level - 1] || t("recipeModal.spiceLevel")
          }
          data-testid={`menu-spice-${item.id}`}
          style={{ color: brandColor || "#b8860b" }}
        >
          {Array.from({ length: item.spice_level }).map((_, i) => (
            <Flame key={i} className="h-3.5 w-3.5" strokeWidth={1.7} />
          ))}
        </span>
      )}
    </div>
  );
};

// A single menu item card — elegant editorial layout, no pricing secrets.
const MenuItem = ({ item, unitSystem, onOpen, brandColor }) => {
  const price = formatPrice(item.price, item.currency_symbol, item.currency_position);
  return (
    <button
      type="button"
      onClick={() => onOpen(item)}
      data-testid={`menu-item-${item.id}`}
      className="group w-full text-left block border-b border-stone-900/10 last:border-b-0 py-8 transition-colors hover:bg-stone-50/40 -mx-6 px-6"
    >
      <div className="flex items-start gap-8">
        {item.image_url ? (
          <div className="hidden sm:block flex-shrink-0 w-24 h-24 overflow-hidden rounded-sm">
            <img
              src={item.image_url}
              alt=""
              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
            />
          </div>
        ) : null}
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-4">
            <h3
              className="flex-1 font-serif text-2xl sm:text-3xl leading-tight tracking-tight text-stone-900"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              {item.name}
            </h3>
            <div
              aria-hidden="true"
              className="flex-shrink-0 hidden sm:block border-b border-dotted border-stone-400/60 flex-1 mb-2 mx-2"
            />
            <span
              className="font-serif text-xl sm:text-2xl text-stone-900 tracking-wide whitespace-nowrap"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
              data-testid={`menu-item-price-${item.id}`}
            >
              {price}
            </span>
          </div>
          {item.notes ? (
            <p
              className="mt-2 text-stone-600 leading-relaxed max-w-prose"
              style={{ fontFamily: "'Manrope', sans-serif" }}
            >
              {item.notes}
            </p>
          ) : null}
          <MenuMetaStrip item={item} brandColor={brandColor} dense />
          {item.ingredients && item.ingredients.length > 0 ? (
            <p
              className="mt-3 text-xs uppercase tracking-[0.22em] text-stone-500"
              style={{ fontFamily: "'Manrope', sans-serif" }}
            >
              {item.ingredients
                .slice(0, 4)
                .map((ing) => {
                  const disp = displayQty(ing.qty, ing.unit, unitSystem);
                  const qtyStr = disp.qty ? `${disp.qty} ${disp.unit}` : "";
                  return qtyStr ? `${ing.name} · ${qtyStr}` : ing.name;
                })
                .join("  ·  ")}
              {item.ingredients.length > 4 ? "  ·  …" : ""}
            </p>
          ) : null}
        </div>
      </div>
    </button>
  );
};

// Detail overlay — opens when a guest taps an item. Still strictly no cost data.
const ItemDetailSheet = ({ item, unitSystem, onClose, brandColor }) => {
  if (!item) return null;
  const price = formatPrice(item.price, item.currency_symbol, item.currency_position);
  return (
    <div
      className="fixed inset-0 z-50 bg-stone-950/60 backdrop-blur-sm flex items-end sm:items-center justify-center"
      onClick={onClose}
      data-testid="menu-item-detail-overlay"
    >
      <div
        className="bg-[#faf7f2] w-full sm:max-w-2xl sm:rounded-sm max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {item.image_url ? (
          <div className="w-full aspect-[16/9] overflow-hidden">
            <img src={item.image_url} alt={item.name} className="w-full h-full object-cover" />
          </div>
        ) : null}
        <div className="p-8 sm:p-12">
          <div className="flex items-start justify-between gap-6 mb-6">
            <h2
              className="font-serif text-4xl sm:text-5xl leading-tight tracking-tight text-stone-900"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              {item.name}
            </h2>
            <button
              onClick={onClose}
              aria-label="Close"
              className="text-stone-500 hover:text-stone-900 text-2xl leading-none"
              data-testid="close-item-detail"
            >
              ×
            </button>
          </div>
          <div className="flex items-center gap-4 mb-4">
            <span
              className="font-serif text-2xl sm:text-3xl text-stone-900"
              style={{ fontFamily: "'Cormorant Garamond', serif", color: brandColor }}
            >
              {price}
            </span>
            {item.department ? (
              <span
                className="text-[0.68rem] uppercase tracking-[0.28em] text-stone-500 border border-stone-300 px-3 py-1"
                style={{ fontFamily: "'Manrope', sans-serif" }}
              >
                {item.department}
              </span>
            ) : null}
          </div>
          <MenuMetaStrip item={item} brandColor={brandColor} />
          <div className="mb-8" />
          {item.notes ? (
            <p
              className="text-stone-700 text-lg leading-relaxed mb-8"
              style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: "italic" }}
            >
              {item.notes}
            </p>
          ) : null}
          {item.ingredients && item.ingredients.length > 0 ? (
            <div className="mb-8">
              <h4
                className="text-xs uppercase tracking-[0.28em] text-stone-500 mb-4"
                style={{ fontFamily: "'Manrope', sans-serif" }}
              >
                Composed with
              </h4>
              <ul className="space-y-2">
                {item.ingredients.map((ing, idx) => {
                  const disp = displayQty(ing.qty, ing.unit, unitSystem);
                  return (
                    <li
                      key={idx}
                      className="flex items-baseline gap-3 text-stone-800"
                      style={{ fontFamily: "'Manrope', sans-serif" }}
                    >
                      <span className="text-stone-400">—</span>
                      <span className="flex-1">{ing.name}</span>
                      {disp.qty ? (
                        <span className="text-stone-500 text-sm tabular-nums">
                          {disp.qty} {disp.unit}
                        </span>
                      ) : null}
                    </li>
                  );
                })}
              </ul>
            </div>
          ) : null}
          {item.method ? (
            <div className="border-t border-stone-200 pt-6">
              <h4
                className="text-xs uppercase tracking-[0.28em] text-stone-500 mb-3"
                style={{ fontFamily: "'Manrope', sans-serif" }}
              >
                From the kitchen
              </h4>
              <p
                className="text-stone-700 leading-relaxed whitespace-pre-line"
                style={{ fontFamily: "'Manrope', sans-serif" }}
              >
                {item.method}
              </p>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

// Demo menu for UI preview (activated via `?demo=1` query param).
// Uses only guest-safe fields — NEVER include cost/margin data.
const DEMO_MENU = {
  restaurant: {
    name: "Maison de Lumière",
    bio: "A boutique kitchen celebrating honey, herbs, and fire. Seasonal menus crafted daily by hand.",
    chef_name: "Chef Krita",
    brand_color: "#b8860b",
    currency: "USD",
    currency_symbol: "$",
    currency_position: "prefix",
  },
  categories: [
    {
      name: "Starters",
      items: [
        {
          id: "demo-1",
          name: "Burrata & Heirloom Tomato",
          category: "Starters",
          department: "Kitchen",
          serving_size: "1",
          price: "18.00",
          currency_symbol: "$",
          currency_position: "prefix",
          notes:
            "Creamy burrata draped over summer heirlooms, basil oil, and a slow drizzle of wild honey.",
          image_url: null,
          method:
            "Hand-tear burrata over slow-ripened heirlooms.\nDress with basil oil.\nFinish with wild honey and flaked sea salt.",
          ingredients: [
            { name: "Heirloom Tomato", qty: "180", unit: "g" },
            { name: "Burrata", qty: "120", unit: "g" },
            { name: "Wild Honey", qty: "10", unit: "ml" },
            { name: "Basil Oil", qty: "15", unit: "ml" },
            { name: "Maldon Salt", qty: "2", unit: "g" },
          ],
        },
        {
          id: "demo-2",
          name: "Charred Octopus, Saffron Aioli",
          category: "Starters",
          department: "Kitchen",
          serving_size: "1",
          price: "24.00",
          currency_symbol: "$",
          currency_position: "prefix",
          notes:
            "Slow-braised, then wood-grilled. Saffron aioli, preserved lemon, and smoked paprika.",
          image_url: null,
          ingredients: [
            { name: "Octopus", qty: "200", unit: "g" },
            { name: "Saffron", qty: "0.2", unit: "g" },
            { name: "Preserved Lemon", qty: "20", unit: "g" },
          ],
        },
      ],
    },
    {
      name: "Mains",
      items: [
        {
          id: "demo-3",
          name: "Honey-Glazed Duck Breast",
          category: "Mains",
          department: "Kitchen",
          serving_size: "1",
          price: "42.00",
          currency_symbol: "$",
          currency_position: "prefix",
          notes:
            "Pan-seared duck breast, honey-thyme glaze, charred stone fruit, pomme puree.",
          image_url: null,
          method:
            "Score duck skin, sear fat-side down until amber.\nRest, then glaze with honey-thyme reduction.\nPlate with pomme puree and charred stone fruit.",
          ingredients: [
            { name: "Duck Breast", qty: "220", unit: "g" },
            { name: "Wild Honey", qty: "20", unit: "ml" },
            { name: "Fresh Thyme", qty: "5", unit: "g" },
            { name: "Stone Fruit", qty: "80", unit: "g" },
            { name: "Potato", qty: "150", unit: "g" },
          ],
        },
        {
          id: "demo-4",
          name: "Line-Caught Sea Bass",
          category: "Mains",
          department: "Kitchen",
          serving_size: "1",
          price: "38.00",
          currency_symbol: "$",
          currency_position: "prefix",
          notes:
            "Brown-butter sea bass, brown-shrimp beurre blanc, sea herbs, Amalfi lemon.",
          image_url: null,
          ingredients: [
            { name: "Sea Bass Fillet", qty: "180", unit: "g" },
            { name: "Brown Shrimp", qty: "40", unit: "g" },
            { name: "Unsalted Butter", qty: "60", unit: "g" },
          ],
        },
      ],
    },
    {
      name: "Desserts",
      items: [
        {
          id: "demo-5",
          name: "Honeycomb Parfait",
          category: "Desserts",
          department: "Pastry",
          serving_size: "1",
          price: "14.00",
          currency_symbol: "$",
          currency_position: "prefix",
          notes:
            "Frozen honey parfait, shards of brittle, whipped crème fraîche, bee-pollen dust.",
          image_url: null,
          ingredients: [
            { name: "Wild Honey", qty: "40", unit: "ml" },
            { name: "Crème Fraîche", qty: "80", unit: "g" },
            { name: "Bee Pollen", qty: "2", unit: "g" },
          ],
        },
      ],
    },
  ],
  total_items: 5,
};


export default function GuestMenu() {
  const { t, i18n } = useTranslation();
  const { id: userId } = useParams();
  const [searchParams] = useSearchParams();
  const demoMode = searchParams.get("demo") === "1";
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [menu, setMenu] = useState(null);
  usePageTitle(menu?.restaurant_name ? `${menu.restaurant_name} · ${t("pageTitles.menu", { defaultValue: "Menu" })}` : "pageTitles.menu");
  const [unitSystem, setUnitSystem] = useState(() => {
    try {
      return localStorage.getItem("plateprofit_guest_unit_system") || "metric";
    } catch {
      return "metric";
    }
  });
  const [activeItem, setActiveItem] = useState(null);
  const [activeCategory, setActiveCategory] = useState(null);

  useEffect(() => {
    try {
      localStorage.setItem("plateprofit_guest_unit_system", unitSystem);
    } catch {
      /* ignore */
    }
  }, [unitSystem]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    if (demoMode) {
      const safe = scrubGuestPayload(DEMO_MENU);
      setMenu(safe);
      if (safe?.categories?.length) setActiveCategory(safe.categories[0].name);
      setLoading(false);
      return () => {
        cancelled = true;
      };
    }

    axios
      .get(`${API}/public/menu/${userId}`)
      .then((res) => {
        if (cancelled) return;
        const safe = scrubGuestPayload(res.data);
        setMenu(safe);
        if (safe?.categories?.length) setActiveCategory(safe.categories[0].name);
      })
      .catch((err) => {
        if (cancelled) return;
        const status = err?.response?.status;
        setError(status === 404 ? "not-found" : "load-failed");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [userId, demoMode]);

  const brandColor = menu?.restaurant?.brand_color || "#b8860b";

  // 🌍 Tourist-Menu localization: when the active locale is non-English AND
  // a recipe has a cached translation for that locale, overlay name + notes.
  // Computed BEFORE early returns so React's hook order stays stable.
  //
  // We preserve the full BCP-47 code for Indic languages (e.g. 'hi-IN')
  // because Sarvam caches under those keys.  For 8-locale global FOH we use
  // the bare 2-letter ISO code (e.g. 'fr').
  const rawLang = i18n.language || "en";
  const activeLng = rawLang.includes("-")
    ? rawLang  // BCP-47 (e.g. hi-IN) — Indic via Sarvam
    : rawLang.toLowerCase();  // ISO-639 (e.g. fr) — Global via Gemini
  const sourceCategoriesForMemo = menu?.categories || [];
  const { categories, hasAnyTranslation } = useMemo(() => {
    if (!sourceCategoriesForMemo || sourceCategoriesForMemo.length === 0) {
      return { categories: [], hasAnyTranslation: false };
    }
    if (activeLng === "en") {
      return { categories: sourceCategoriesForMemo, hasAnyTranslation: false };
    }
    let any = false;
    const localized = sourceCategoriesForMemo.map((cat) => ({
      ...cat,
      items: (cat.items || []).map((item) => {
        const cached = item?.translations?.[activeLng];
        if (cached && (cached.name || cached.notes)) {
          any = true;
          return {
            ...item,
            name: cached.name || item.name,
            notes: cached.notes || item.notes,
            _translated: true,
          };
        }
        return item;
      }),
    }));
    return { categories: localized, hasAnyTranslation: any };
  }, [sourceCategoriesForMemo, activeLng]);

  const scrollToCategory = (name) => {
    setActiveCategory(name);
    const el = document.getElementById(`guest-section-${name}`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  if (loading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center bg-[#faf7f2]"
        data-testid="guest-menu-loading"
      >
        <div
          className="text-stone-500 text-lg tracking-[0.3em] uppercase"
          style={{ fontFamily: "'Manrope', sans-serif" }}
        >
          {t("guestMenu.loading")}
        </div>
      </div>
    );
  }

  if (error === "not-found") {
    return (
      <div
        className="min-h-screen flex items-center justify-center bg-[#faf7f2] px-6 text-center"
        data-testid="guest-menu-not-found"
      >
        <div>
          <h1
            className="font-serif text-5xl text-stone-900 mb-4"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            {t("guestMenu.kitchenResting")}
          </h1>
          <p
            className="text-stone-600"
            style={{ fontFamily: "'Manrope', sans-serif" }}
          >
            {t("guestMenu.notPublishedYet")}
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div
        className="min-h-screen flex items-center justify-center bg-[#faf7f2] px-6 text-center"
        data-testid="guest-menu-error"
      >
        <div>
          <h1
            className="font-serif text-4xl text-stone-900 mb-4"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            {t("guestMenu.loadFailed")}
          </h1>
          <p
            className="text-stone-600"
            style={{ fontFamily: "'Manrope', sans-serif" }}
          >
            {t("guestMenu.loadFailedHint")}
          </p>
        </div>
      </div>
    );
  }

  const restaurant = menu?.restaurant || {};

  return (
    <div
      className="min-h-screen bg-[#faf7f2] text-stone-900"
      style={{ fontFamily: "'Manrope', sans-serif" }}
      data-testid="guest-menu-page"
    >
      {/* Grain texture overlay */}
      <div
        aria-hidden="true"
        className="pointer-events-none fixed inset-0 opacity-[0.035] mix-blend-multiply"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")",
        }}
      />

      {/* HERO */}
      <header className="relative px-6 pt-16 sm:pt-24 pb-10 text-center">
        {/* Viewer-driven language switcher — pinned top-right of hero so a
            foreign tourist can translate the local menu into their own
            language on the fly without touching the restaurant owner's
            preferred_language. */}
        <div className="absolute top-6 right-6 z-10" data-testid="guest-menu-language-switcher">
          <GlobalLanguageSwitcher variant="light" align="end" compact flattenIndic />
        </div>

        <div className="max-w-3xl mx-auto">
          {restaurant.logo_base64 ? (
            <img
              src={
                restaurant.logo_base64.startsWith("data:")
                  ? restaurant.logo_base64
                  : `data:image/png;base64,${restaurant.logo_base64}`
              }
              alt=""
              className="mx-auto mb-8 h-20 w-20 sm:h-24 sm:w-24 object-contain"
              data-testid="guest-menu-logo"
            />
          ) : null}
          <div
            className="text-xs uppercase tracking-[0.4em] mb-4"
            style={{ color: brandColor }}
          >
            {t("guestMenu.alaCarte")}
          </div>
          <h1
            className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-[1.05] tracking-tight"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
            data-testid="guest-menu-restaurant-name"
          >
            {restaurant.name || t("guestMenu.defaultRestaurantName")}
          </h1>
          {restaurant.chef_name ? (
            <div
              className="mt-4 text-sm tracking-[0.3em] uppercase text-stone-500"
              data-testid="guest-menu-chef-name"
            >
              {t("guestMenu.curatedBy", { chef: restaurant.chef_name })}
            </div>
          ) : null}
          {restaurant.bio ? (
            <p
              className="mt-8 text-lg sm:text-xl italic text-stone-600 leading-relaxed max-w-2xl mx-auto"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
              data-testid="guest-menu-bio"
            >
              “{restaurant.bio}”
            </p>
          ) : null}
          <div
            aria-hidden="true"
            className="mx-auto mt-10 h-px w-32"
            style={{ backgroundColor: brandColor, opacity: 0.4 }}
          />
        </div>
      </header>

      {/* CONTROLS BAR — unit system + category filter */}
      <div className="sticky top-0 z-30 backdrop-blur-md bg-[#faf7f2]/80 border-y border-stone-900/10">
        <div className="max-w-5xl mx-auto px-6 py-4 flex flex-wrap items-center justify-between gap-4">
          {/* Category pills */}
          <div
            className="flex gap-1 overflow-x-auto scrollbar-none -mx-2 px-2"
            data-testid="guest-menu-category-tabs"
          >
            {categories.map((c) => {
              const isActive = activeCategory === c.name;
              return (
                <button
                  key={c.name}
                  type="button"
                  onClick={() => scrollToCategory(c.name)}
                  data-testid={`guest-menu-category-${c.name}`}
                  className="whitespace-nowrap px-4 py-2 text-xs uppercase tracking-[0.22em] transition-colors"
                  style={{
                    color: isActive ? brandColor : "#78716c",
                    borderBottom: `1px solid ${isActive ? brandColor : "transparent"}`,
                  }}
                >
                  {c.name}
                </button>
              );
            })}
          </div>

          {/* Metric / Imperial toggle */}
          <div
            className="flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-stone-500"
            data-testid="guest-menu-unit-toggle"
          >
            <button
              type="button"
              onClick={() => setUnitSystem("metric")}
              data-testid="unit-toggle-metric"
              className="px-3 py-1 transition-colors"
              style={{
                color: unitSystem === "metric" ? "#faf7f2" : "#78716c",
                backgroundColor: unitSystem === "metric" ? brandColor : "transparent",
              }}
            >
              {t("guestMenu.metric")}
            </button>
            <span className="text-stone-300">/</span>
            <button
              type="button"
              onClick={() => setUnitSystem("imperial")}
              data-testid="unit-toggle-imperial"
              className="px-3 py-1 transition-colors"
              style={{
                color: unitSystem === "imperial" ? "#faf7f2" : "#78716c",
                backgroundColor: unitSystem === "imperial" ? brandColor : "transparent",
              }}
            >
              {t("guestMenu.imperial")}
            </button>
          </div>
        </div>
      </div>

      {/* MENU BODY */}
      <main className="relative max-w-4xl mx-auto px-6 py-16">
        {hasAnyTranslation && (
          <div
            className="mb-10 -mt-6 flex items-center justify-center gap-2 px-4 py-2 rounded-full border border-amber-300/50 bg-amber-100/30 text-[0.7rem] uppercase tracking-[0.22em] text-stone-600 max-w-fit mx-auto"
            data-testid="guest-menu-translated-banner"
            style={{ fontFamily: "'Manrope', sans-serif" }}
          >
            <Sparkles className="h-3.5 w-3.5" style={{ color: brandColor }} />
            {t("guestMenu.translatedBadge")}
          </div>
        )}
        {categories.length === 0 ? (
          <p
            className="text-center text-stone-500 italic"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            {t("guestMenu.emptyMenu")}
          </p>
        ) : (
          categories.map((cat) => (
            <section
              key={cat.name}
              id={`guest-section-${cat.name}`}
              className="mb-16 last:mb-0 scroll-mt-24"
              data-testid={`guest-menu-section-${cat.name}`}
            >
              <div className="flex items-center gap-4 mb-8">
                <div
                  aria-hidden="true"
                  className="h-px flex-1"
                  style={{ backgroundColor: brandColor, opacity: 0.35 }}
                />
                <h2
                  className="font-serif text-3xl sm:text-4xl tracking-wider uppercase"
                  style={{
                    fontFamily: "'Cormorant Garamond', serif",
                    color: brandColor,
                    letterSpacing: "0.15em",
                  }}
                >
                  {cat.name}
                </h2>
                <div
                  aria-hidden="true"
                  className="h-px flex-1"
                  style={{ backgroundColor: brandColor, opacity: 0.35 }}
                />
              </div>
              <div>
                {cat.items.map((item) => (
                  <MenuItem
                    key={item.id}
                    item={item}
                    unitSystem={unitSystem}
                    onOpen={setActiveItem}
                    brandColor={brandColor}
                  />
                ))}
              </div>
            </section>
          ))
        )}
      </main>

      {/* FOOTER */}
      <footer
        className="relative border-t border-stone-900/10 py-10 text-center text-xs uppercase tracking-[0.28em] text-stone-500"
        style={{ fontFamily: "'Manrope', sans-serif" }}
      >
        <div className="max-w-3xl mx-auto px-6">
          <div>{t("guestMenu.footerPrices", { currency: restaurant.currency || "USD" })}</div>
          <div className="mt-3 text-stone-400">
            {t("guestMenu.craftedWith")}
          </div>
        </div>
      </footer>

      <ItemDetailSheet
        item={activeItem}
        unitSystem={unitSystem}
        brandColor={brandColor}
        onClose={() => setActiveItem(null)}
      />
    </div>
  );
}
